import React, { useEffect } from 'react';
import * as Sentry from '@sentry/react';
import Navigation from '@layouts/PrimaryLayout/Navigation';
import layoutStyles from '@layouts/PrimaryLayout/PrimaryLayout.module.scss';
import ViewMessage from '@components/ViewMessage';
import { useDispatch, useSelector } from 'react-redux';
import { closeModal, modalStateSelector } from '@actions/ui';
import { UnauthenticatedLayout } from '@layouts/MasterLayout';
import { CircledExclamationPointIcon } from '@components/Icons';
import { supportHref } from '@support/preferred-email-client';

export const GenericErrorMessage = () => (
  <ViewMessage
    headline="Sorry, something went wrong"
    message={
      <>
        Please try again or feel free to <br /> <a href={supportHref()}>reach out to us</a>.
      </>
    }
    icon={<CircledExclamationPointIcon />}
    button={{
      text: 'Refresh',
      onClick: () => (location.href = '/'),
    }}
    canShowNavBarToggle
  />
);

export const AppErrorMessage = () => (
  <UnauthenticatedLayout>
    <GenericErrorMessage />
  </UnauthenticatedLayout>
);

export const ErrorMessageWithNav = () => {
  const dispatch = useDispatch();
  const { isOpen: modalIsOpen } = useSelector(modalStateSelector);

  // If an error thrown in a modal causes this view to be rendered, we
  // need to make sure the problematic modal isn't going to render when a user
  // tries to navigate away from this error UI. The location change will cause the
  // modal to close, but not before it is rendered again -- the user would
  // end up back in this error UI and would need to click to navigate a second time.
  useEffect(() => {
    if (modalIsOpen) {
      dispatch(closeModal);
    }
  }, []);

  return (
    <div className={layoutStyles.container}>
      <Navigation />
      <GenericErrorMessage />
    </div>
  );
};

const EmptyErrorMessage = () => null;

export function EmptyErrorBoundary({ children }) {
  return (
    <Sentry.ErrorBoundary fallback={EmptyErrorMessage}>{children || null}</Sentry.ErrorBoundary>
  );
}

export function ContentErrorBoundary({ children }) {
  return <Sentry.ErrorBoundary fallback={GenericErrorMessage}>{children}</Sentry.ErrorBoundary>;
}

export function AppErrorBoundary({ children }) {
  return <Sentry.ErrorBoundary fallback={AppErrorMessage}>{children}</Sentry.ErrorBoundary>;
}
